import type { Cohort, SegmentSummary } from "@/application/signals/types";
import { SignalsCard, EmptyState } from "./SignalsCard";
import {
  formatCount,
  formatDays,
  formatGpv,
  formatPercent,
  formatTier,
} from "../format";

/**
 * Conversion quality by segment. Sorted by activation rate rather than size, so
 * a small segment that converts well is not buried under a large one that does
 * not — which is the whole question this card exists to answer.
 */
export function ConversionCard({
  segments,
  baseActivationRate,
  onDrill,
}: {
  segments: SegmentSummary[];
  baseActivationRate: number;
  onDrill: (cohort: Cohort) => void;
}) {
  const ranked = [...segments].sort(
    (a, b) => b.activationRate - a.activationRate,
  );

  return (
    <SignalsCard
      title="Conversion by segment"
      subtitle={`Base activation rate ${formatPercent(baseActivationRate)}`}
    >
      {ranked.length === 0 ? (
        <EmptyState message="No segments in this build" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-xs">
                <th className="px-4 py-2 text-left font-medium">Segment</th>
                <th className="px-3 py-2 text-right font-medium">Merchants</th>
                <th className="px-3 py-2 text-right font-medium">Activated</th>
                <th className="px-3 py-2 text-right font-medium">
                  Median to 1st txn
                </th>
                <th className="px-3 py-2 text-right font-medium">GPV</th>
                <th className="px-3 py-2 text-right font-medium">Solo</th>
                <th className="px-4 py-2 text-right font-medium">Stalled</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((segment) => {
                const stalled = segment.merchants - segment.activated;
                const beatsBase = segment.activationRate > baseActivationRate;
                return (
                  <tr
                    key={segment.segment}
                    className="border-border hover:bg-muted cursor-pointer border-b last:border-0"
                    onClick={() =>
                      onDrill({
                        label: segment.label,
                        filter: { segment: segment.segment },
                      })
                    }
                  >
                    <td className="px-4 py-2.5">
                      <span className="text-foreground">{segment.label}</span>
                      <span className="text-subtle-foreground ml-2 text-xs">
                        {formatTier(segment.tier)}
                      </span>
                    </td>
                    <td className="text-muted-foreground px-3 py-2.5 text-right tabular-nums">
                      {formatCount(segment.merchants)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right tabular-nums ${
                        beatsBase
                          ? "font-medium text-emerald-600"
                          : "text-muted-foreground"
                      }`}
                    >
                      {formatPercent(segment.activationRate)}
                    </td>
                    <td className="text-muted-foreground px-3 py-2.5 text-right tabular-nums">
                      {formatDays(segment.medianDaysToFirstTxn)}
                    </td>
                    <td className="text-muted-foreground px-3 py-2.5 text-right tabular-nums">
                      {formatGpv(segment.gpvByCurrency)}
                    </td>
                    <td className="text-muted-foreground px-3 py-2.5 text-right tabular-nums">
                      {formatPercent(segment.soloShare)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {stalled > 0 ? (
                        <button
                          type="button"
                          className="cursor-pointer text-blue-600 tabular-nums hover:underline"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDrill({
                              label: `${segment.label} · not activated`,
                              filter: {
                                segment: segment.segment,
                                activated: false,
                              },
                            });
                          }}
                        >
                          {formatCount(stalled)}
                        </button>
                      ) : (
                        <span className="text-subtle-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SignalsCard>
  );
}
